import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material';

import { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useDialog } from '@/logic/state';
import { CloseButton } from '@/components/shared/close-button';
import ImageBoxFuture from '@/components/shared/image-box-future';

const EmptyFilterIcon = '/icons/movie-finder/empty-filter-icon.svg';

export const ChangeShowTimesDialog: FC = () => {
  const theme = useTheme();
  const intl = useIntl();
  const [dialog, setDialog] = useDialog(s => [s.dialog, s.setDialog], shallow);
  const [dialogMessage, setDialogMessage] = useState<{
    title: string;
    description: string;
  }>(null);

  const handle = useCallback(
    (allow: boolean) => {
      setDialog(prevState => ({
        wrapper: false,
        isAllowedToChangeShowTimesDialogPayload: {
          ...prevState?.isAllowedToChangeShowTimesDialogPayload,
        },
      }));
      setDialog(prevState => ({
        wrapper: false,
        isAllowedToChangeShowTimesDialog: true,
        isAllowedToChangeShowTimesDialogPayload: {
          ...prevState?.isAllowedToChangeShowTimesDialogPayload,
          isAllowed: allow,
        },
      }));
    },
    [setDialog]
  );

  useEffect(() => {
    if (dialog?.isAllowedToChangeShowTimesDialogPayload?.date) {
      setDialogMessage({
        title: 'movie.finder.filters.change_date',
        description: 'movie.finder.filters.change_date_desc',
      });
    } else {
      setDialogMessage({
        title: 'movie.finder.filters.change.showtime',
        description: 'movie.finder.filters.change_showtime_desc',
      });
    }
  }, [dialog?.isAllowedToChangeShowTimesDialogPayload]);

  return (
    <>
      <DialogTitle>
        <CloseButton handleClick={() => handle(false)} />
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flexGrow: 1,
          }}
        >
          <Box sx={{ marginBlock: 4 }}>
            <ImageBoxFuture
              img={EmptyFilterIcon}
              title={intl.formatMessage({
                id: 'alt.empty_filters',
              })}
              objectFit='contain'
              sx={{
                width: pxToRem(64),
                height: pxToRem(72),
              }}
            />
          </Box>
          <Typography
            sx={{
              color: theme.palette.grey[90],
              fontSize: pxToRem(22),
              fontWeight: 700,
              lineHeight: '33px',
              marginBottom: pxToRem(24),
            }}
          >
            {dialogMessage?.title && <FormattedMessage id={dialogMessage.title} />}
          </Typography>
          <Typography
            sx={{
              color: theme.palette.grey[70],
              fontSize: pxToRem(18),
              fontWeight: 400,
              lineHeight: '27px',
              textAlign: 'center',
            }}
          >
            {dialogMessage?.description && (
              <FormattedMessage id={dialogMessage.description} />
            )}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          id='filter-change-cancel'
          fullWidth
          sx={{
            fontFamily: theme.palette.typography.font.bold,
          }}
          onClick={() => handle(false)}
        >
          <FormattedMessage id='global.cancel' />
        </Button>
        <Button
          id='filter-change-okay'
          variant='contained'
          fullWidth
          onClick={() => handle(true)}
          sx={{ height: { tablet: pxToRem(56), mobile: '56px' } }}
        >
          <FormattedMessage id='global.yes' />
        </Button>
      </DialogActions>
    </>
  );
};
